import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Layout } from '@common/data-models/service/layout';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ServiceLayoutColumn } from '@common/data-models/service/service-layout-column';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ObjectTypes } from '@common/enums/object-types';
import { BasicContentService } from '@common/services/basic-content-service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LayoutState, ViewStateRecordsAlias } from '@common/services/layout-state';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Util } from '@common/utils/util';
import { Store } from '@ngrx/store';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { NOMDatabase } from '@src/suite/common/store/records/records.models';
import { AppService } from '@suite/app.service';

import { LiveSearchService } from './live-search.service';

@Injectable({ providedIn: 'root' })
export class RundownService {
    private appService: AppService = inject(AppService);
    private nomWebServicesJson: NomWebServicesJson = inject(NomWebServicesJson);
    private globalTablesService: GlobalTablesService = inject(GlobalTablesService);
    private visualStateService: VisualStateService = inject(VisualStateService);
    private viewStateService: LayoutState = inject(LayoutState);
    private store: Store = inject(Store);
    private basicContentService = inject(BasicContentService);
    private liveSearchService = inject(LiveSearchService);

    constructor() {
        // TODO: This is a hack to get the filters for the view state type
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.ROLIST] = this.getFiltersForRundown().bind(this);
    }

    public getFiltersForRundown(): (item: FieldDef) => boolean {
        return (item: FieldDef) => {
            if (item.Briefable && (item.RundownProp === true || item.Type?.includes('RO Property'))) {
                return true;
            }
            return false;
        };
    }

    public async loadRundownsV2(vs: ViewStateRecordsAlias, database: NOMDatabase) {
        return this.liveSearchService.getSearchResults(vs, database);
    }

    public async createStory(database: string, path: string, collectionGuid: string, fields: { [id: string]: any }): Promise<ServiceResponse<StorytellingRundownRecord>> {
        let requestParams = {};
        Util.escapeSpecialCharsForFileName(fields);
        const fieldsToUpdate = null;
        requestParams = {
            collectionGuid: collectionGuid,
            collectionPath: path,
            database: database,
            fieldsToUpdate: fieldsToUpdate,
            insertBeforeGuid: '',
            returnText: false,
        };
        try {
            await this.nomWebServicesJson.patch('/api/RundownContent', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to create story record!',
                detailMessage: `Failed to create story record! \n\rStackTrace: NomWebServicesJson.createStory(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingRundownRecord>(false, null);
        }
        // const serviceRecord = ServiceCollectionRecord.instance(response.body);
        // const converted = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord(serviceRecord);

        // await DataStore.setCollection(converted, true);
        // return new ServiceResponse<RundownRecord>(true, DataStore.byGuid<RundownRecord>(converted.guid));
        return new ServiceResponse<StorytellingRundownRecord>(true, null);
    }

    public async getRundown(database: string, path: string, guid: string): Promise<ServiceResponse<StorytellingRundownRecord>> {
        let response: HttpResponse<ServiceCollectionRecord>;
        try {
            response = await this.nomWebServicesJson.get<ServiceCollectionRecord>('/api/RundownContent', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    database: database,
                    path: path,
                    guid: guid,
                    hitHighlightTerm: '',
                    returnText: 'true',
                },
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load rundown content!',
                detailMessage: `Failed to load rundown content! \n\rStackTrace: NomWebServicesJson.getRundown(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingRundownRecord>(false, null);
        }

        const serviceRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(response.body);
        const converted = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2(serviceRecord);

        return new ServiceResponse<StorytellingRundownRecord>(true, converted);
    }

    // get stored layouts
    public async getStoredLayouts(): Promise<any> {
        try {
            return await this.nomWebServicesJson.get<string[]>('/api/Layouts', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                params: {
                    objectType: ObjectTypes.RUNDOWN,
                },
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to get stored layouts!',
                detailMessage: `Failed to get stored layouts! \n\rStackTrace: RundownServices.getStoredLayouts(); \n\rError: ${error}`,
            });
            return new ServiceResponse<any>(false);
        }
    }

    public async getLayout(type: number, name: string = ''): Promise<any> {
        const params = { LayoutType: type, CollectionType: ObjectTypes.RUNDOWN };
        if (name !== '') {
            params['LayoutName'] = name;
        }
        try {
            const ret = await this.nomWebServicesJson.post<ServiceLayoutColumn[]>('/api/Layouts', params, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
            const converted = this.nomWebServicesJson.convertor.fromServiceLayout(ret.body, type);
            return new ServiceResponse<Layout>(true, converted);
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to get a layout!',
                detailMessage: `Failed to get a layout! \n\rStackTrace: RundownServices.getLayout(); \n\rError: ${error}`,
            });
            return new ServiceResponse<Layout>(false);
        }
    }

    public async freezePages(ro: StorytellingRundownRecord): Promise<ServiceResponse<{ Pages: string[]; PagesFrozen: boolean }>> {
        try {
            // TODO: Make proper type for freezing pages
            const ret = await this.nomWebServicesJson.patch<{ Pages: string[]; PagesFrozen: boolean }>('/api/RundownContent', null, {
                params: {
                    database: ro.Database,
                    path: ro.Path.fullPath,
                    guid: ro.Guid,
                    freezePages: !ro.FreezePages,
                },
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
            console.log(ret.body);
            return new ServiceResponse<{ Pages: string[]; PagesFrozen: boolean }>(true, { Pages: ret.body.Pages, PagesFrozen: ret.body.PagesFrozen });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to get a layout!',
                detailMessage: `Failed to get a layout! \n\rStackTrace: RundownServices.getLayout(); \n\rError: ${error}`,
            });
            return new ServiceResponse<{ Pages: string[]; PagesFrozen: boolean }>(false);
        }
    }

    public async updateRundown(ro: StorytellingRundownRecord, data: { [id: string]: unknown }): Promise<StorytellingRundownRecord> {
        try {
            const ret = await this.basicContentService.updateBasicContentV2<StorytellingRundownRecord>(ro.Database, ro.Path.fullPath, ro.Guid, false, data, false, ro);
            return ret.data;
        } catch (error) {
            console.log(error);
        }
    }

    public async addStoryToRundown(
        ro: StorytellingRundownRecord,
        scriptDatabase: string,
        scriptPath: string,
        scriptGuid: string,
        insertBeforeGuid: string,
        fieldsToUpdate: { [id: string]: any }
    ): Promise<ServiceResponse<StorytellingRundownRecord>> {
        try {
            // TODO: Make proper type for this
            const ret = await this.nomWebServicesJson.post<any>(
                '/api/MoveScriptToCollection',
                {
                    RODatabase: ro.Database,
                    ROPath: ro.Path.fullPath,
                    ROGuid: ro.Guid,
                    ScriptDatabase: scriptDatabase,
                    ScriptPath: scriptPath,
                    ScriptGuid: scriptGuid,
                    InsertBeforeGuid: insertBeforeGuid,
                    // TODO: This probably has some logic around it that I dont know
                    KeepApproved: false,
                    PropertiesOverride: this.nomWebServicesJson.convertor.toENPSFields(fieldsToUpdate),
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                    }),
                    responseType: 'json',
                    observe: 'response',
                }
            );

            const serviceRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(ret.body);
            const converted = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2(serviceRecord) as StorytellingRundownRecord;
            return new ServiceResponse(true, converted);
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to get a layout!',
                detailMessage: `Failed to get a layout! \n\rStackTrace: RundownServices.getLayout(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingRundownRecord>(false);
        }
    }

    public async insertRowInRundown(
        ro: StorytellingRundownRecord,
        database: string,
        path: string,
        guid: string,
        insertBeforeGuid: string,
        fieldsToUpdate: { [id: string]: any }
    ): Promise<ServiceResponse<StorytellingRundownRecord>> {
        try {
            // TODO: Make proper type for this
            const ret = await this.nomWebServicesJson.patch<any>(
                '/api/RundownContent',
                {
                    database,
                    collectionPath: path,
                    collectionGuid: guid,
                    insertBeforeGuid: insertBeforeGuid,
                    fieldsToUpdate: this.nomWebServicesJson.convertor.toENPSFields(fieldsToUpdate),
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                    }),
                    responseType: 'json',
                    observe: 'response',
                }
            );

            const serviceRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(ret.body);
            const converted = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2(serviceRecord) as StorytellingRundownRecord;
            // TODO
            // DataStore.setCollection(converted, true);
            return new ServiceResponse(true, converted);
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to get a layout!',
                detailMessage: `Failed to get a layout! \n\rStackTrace: RundownServices.getLayout(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingRundownRecord>(false);
        }
    }
}
